import { useEffect, useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Invoice {
  id: string;
  number: string | null;
  created: number;
  amount_paid: number;
  amount_due: number;
  currency: string;
  status: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

const formatAmount = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format(cents / 100);

export function RecentInvoices() {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('list-invoices');
        if (error) throw error;
        if (!cancelled) setInvoices(data?.invoices ?? []);
      } catch (e) {
        console.error('list-invoices error:', e);
        if (!cancelled) setInvoices([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="pt-4 mt-4 border-t border-border space-y-3">
      <h4 className="font-display font-bold text-sm flex items-center gap-2">
        <FileText className="w-4 h-4 text-primary" />
        Recent Invoices
      </h4>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading invoices…
        </div>
      )}

      {!loading && invoices && invoices.length === 0 && (
        <p className="text-xs text-muted-foreground py-2">No invoices yet.</p>
      )}

      {!loading && invoices && invoices.length > 0 && (
        <ul className="space-y-1.5">
          {invoices.map((inv) => {
            const amount = inv.amount_paid > 0 ? inv.amount_paid : inv.amount_due;
            const date = new Date(inv.created * 1000).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
            });
            const downloadUrl = inv.invoice_pdf || inv.hosted_invoice_url;
            return (
              <li
                key={inv.id}
                className="flex items-center justify-between gap-3 p-2.5 rounded-xl bg-muted/40"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-display font-bold truncate">{date}</p>
                  <p className="text-[11px] text-muted-foreground capitalize">
                    {inv.status ?? 'unknown'}{inv.number ? ` · ${inv.number}` : ''}
                  </p>
                </div>
                <span className="text-sm font-display font-bold tabular-nums">
                  {formatAmount(amount, inv.currency)}
                </span>
                {downloadUrl ? (
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-border hover:bg-muted transition-colors"
                    aria-label="Download invoice"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <span className="inline-flex items-center justify-center h-8 w-8 opacity-30">
                    <Download className="w-3.5 h-3.5" />
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
